'use client';

import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import Input from '@/components/theme/Input';
import ErrorText from '@/components/theme/ErrorText';

import { getGuestWelcomeMessage, Settings, GuestBookContent } from '@/models';
import { getInputDateFormat } from '@/lib/dateFormat';

import styles from './content.module.scss';
import { HeroLayout } from '@/components/core/layout';
import { Warning } from '@/components/warning';
import { useLogin, useKioskLogin } from './hooks';
import { Button, DatePicker, Stack, Text, VStack } from '@ui-library';
import { LOGIN_STEP } from './hooks/types';
import PropertyLogo from '@/components/logo/PropertyLogo';
import { ReservationOptionCard } from '@/components/reservation';

interface LoginPageContentProps {
  company: string;
  redirect?: string;
  content?: GuestBookContent | null;
  settings: Settings;
  kioskMode?: boolean;
}

const LoginPageContent = ({
  company,
  redirect,
  content,
  settings,
  useLoginHook,
}: LoginPageContentProps & {
  useLoginHook: typeof useLogin | typeof useKioskLogin;
}) => {
  const intl = useIntl();
  const {
    state,
    setStateField,
    isSubmitDisabled,
    onSubmit,
    canGoBack,
    onBack,
    currentStep,
    onSelectReservation,
  } = useLoginHook({
    propertyId: company,
    redirect,
  });

  const {
    firstName,
    lastName,
    reservationId,
    arrivalDate,
    reservationNotFound,
    guestNotFound,
    loading,
    errorText,
    matchingReservations,
  } = state;

  const heroSection = useMemo(
    () => ({
      settings: {
        image_url: content?.thumbnail || settings.whistle_guest_hero,
      },
      plain_text:
        getGuestWelcomeMessage(content) ?? settings.whistle_guest_company_name,
    }),
    [content, settings],
  );

  const renderInput = () => {
    const FirstNameInput = (
      <Input
        label={intl.formatMessage({
          id: 'common/first_name',
          defaultMessage: 'First name',
        })}
        onChange={(evt: any) => {
          setStateField('firstName', evt.target.value);
        }}
        value={firstName}
        required
      />
    );

    const LastNameInput = (
      <Input
        label={intl.formatMessage({
          id: 'common/last_name',
          defaultMessage: 'Last name',
        })}
        onChange={(evt: any) => {
          setStateField('lastName', evt.target.value);
        }}
        value={lastName}
        required
      />
    );

    const ReservationIdInput = (
      <>
        <Input
          label={intl.formatMessage({
            id: 'common/reservation_number',
            defaultMessage: 'Reservation number',
          })}
          onChange={(evt: any) => {
            setStateField('reservationId', evt.target.value);
          }}
          value={reservationId}
          required
        />
        <div className={styles.inputDescription}>
          <span>
            {intl.formatMessage({
              id: 'reservation_number_description',
              defaultMessage:
                'Sometimes called booking id or confirmation code.',
            })}
          </span>
        </div>
      </>
    );

    const ArrivalDateInput = (
      <VStack alignItems="center" gap="3">
        <Text fontWeight={'semibold'} alignSelf="flex-start">
          {intl.formatMessage({
            id: 'common/arrival_date',
            defaultMessage: 'Arrival date',
          })}
        </Text>
        <DatePicker
          dateFormat={
            settings.date_format
              ? getInputDateFormat(settings.date_format)
              : undefined
          }
          value={arrivalDate ? new Date(`${arrivalDate}T12:00:00`) : undefined}
          onChange={(val: any) => {
            try {
              if (!val) {
                return setStateField('arrivalDate', '');
              }

              const year = val.getFullYear();
              const month = String(val.getMonth() + 1).padStart(2, '0');
              const day = String(val.getDate()).padStart(2, '0');

              const formattedDate = `${year}-${month}-${day}`;

              setStateField('arrivalDate', formattedDate);
            } catch (e) {
              console.error('Error formatting date:', e);
            }
          }}
        />
      </VStack>
    );

    const GuestNotFound = guestNotFound && (
      <Warning
        title={intl.formatMessage({
          id: 'guest_not_found',
          defaultMessage: 'Guest not found',
        })}
        message={intl.formatMessage({
          id: 'guest_not_found_message',
          defaultMessage:
            'Please ensure your details match the details entered during booking.',
        })}
      />
    );

    const ReservationNotFound = reservationNotFound && (
      <Warning
        title={intl.formatMessage({
          id: 'reservation_not_found',
          defaultMessage: 'Reservation not found',
        })}
        message={intl.formatMessage({
          id: 'reservation_not_found_message',
          defaultMessage:
            'Please ensure your details match the details entered during booking.',
        })}
      />
    );

    const SelectReservationInput = (
      <VStack gap="2" w="full" h="sm" overflowY="auto">
        {matchingReservations.map((reservation) => (
          // Check-in settings require a guest token, unavailable before login.
          <ReservationOptionCard
            key={reservation.reservationId}
            reservation={reservation}
            displayGuestRoomNumber={false}
            primaryColor={settings.whistle_guest_primary_button_color}
            companyTimezone={settings.whistle_guest_company_timezone}
            propertyDateFormat={settings.date_format}
            onClick={onSelectReservation}
          />
        ))}
      </VStack>
    );

    const getCurrentInput = () => {
      switch (currentStep) {
        case LOGIN_STEP.LASTNAME:
          return LastNameInput;
        case LOGIN_STEP.FIRSTNAME:
          return FirstNameInput;
        case LOGIN_STEP.ARRIVALDATE:
          return ArrivalDateInput;
        case LOGIN_STEP.SELECT_RESERVATION:
          return SelectReservationInput;
        case LOGIN_STEP.RESERVATIONID:
          return ReservationIdInput;
        default:
          return null;
      }
    };

    return (
      <>
        {GuestNotFound}
        {ReservationNotFound}
        <section>{getCurrentInput()}</section>
      </>
    );
  };

  return (
    <HeroLayout section={heroSection}>
      <main>
        <form className={styles.form} onSubmit={onSubmit}>
          <PropertyLogo settings={settings} variant="md" />
          <Text fontWeight={'semibold'} fontSize="lg">
            {currentStep === LOGIN_STEP.SELECT_RESERVATION
              ? intl.formatMessage({
                  id: 'select_your_reservation',
                  defaultMessage: 'Select your reservation',
                })
              : intl.formatMessage({
                  id: 'sign_in_to_view_your_reservation',
                  defaultMessage: 'Sign in to view your reservation',
                })}
          </Text>
          {renderInput()}
          <Stack w={'full'} direction={{ base: 'row', md: 'column' }} gap={2}>
            {currentStep !== LOGIN_STEP.SELECT_RESERVATION && (
              <Button
                type="submit"
                loading={loading}
                disabled={isSubmitDisabled}
                flex={{ base: 1, md: undefined }}
                background={settings.whistle_guest_primary_button_color}
                color={settings.whistle_guest_secondary_button_color}
                minH={'10'}
                order={{ base: 1, md: 1 }}
              >
                {intl.formatMessage({
                  id: 'common/continue',
                  defaultMessage: 'Continue',
                })}
              </Button>
            )}
            {canGoBack && (
              <Button
                onClick={onBack}
                loading={
                  currentStep === LOGIN_STEP.SELECT_RESERVATION && loading
                }
                flex={{ base: 1, md: undefined }}
                variant="outline"
                minH={'10'}
                order={{ base: 0, md: 2 }}
              >
                {intl.formatMessage({
                  id: 'common/back',
                  defaultMessage: 'Back',
                })}
              </Button>
            )}
          </Stack>
          {errorText && <ErrorText>{errorText}</ErrorText>}
        </form>
      </main>
    </HeroLayout>
  );
};

const LoginPageContentWrapper = (props: LoginPageContentProps) => {
  return (
    <LoginPageContent
      {...props}
      useLoginHook={props.kioskMode ? useKioskLogin : useLogin}
    />
  );
};

export default LoginPageContentWrapper;
